import { Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getAvailablePlansRequest,
	getCurrentPlanRequest,
	getRequestedPlanRequest,
} from 'redux/reducers/pricing/reducer';
import {
	getAvailablePlansLoader,
	getAvailablePlans,
	getCurrentPlan,
	getCurrentPlanLoader,
	getRequestedPlan,
	getRequestedPlanLoader,
} from 'redux/reducers/pricing/selectors';
import { DateTime } from 'luxon';
import { filters } from './pricingData';
import { PricingCardSmall } from './PricingCardSmall';
import { PricingCardBig } from './PricingCardBigRightPrice';
import { PlanFilter } from './PlanFilter';
import { BlockRequestBanner } from './BlockRequestBanner';
import { SHOPIFY_PRICING_URL } from 'layouts/Header/constants';

export const Pricing: FC = () => {
	const dispatch = useDispatch();

	const currentPlan = useSelector(getCurrentPlan);
	const availablePlansLoader = useSelector(getAvailablePlansLoader);
	const currentPlanLoader = useSelector(getCurrentPlanLoader);
	const availablePlans = useSelector(getAvailablePlans);
	const requestedPlan = useSelector(getRequestedPlan);
	const requestedPlanLoader = useSelector(getRequestedPlanLoader);
	const requestedPlanData =
		availablePlans?.find((item) => item.id === requestedPlan?.newMonthlyFeeTypeId) ?? null;

	const [checkedArray, setCheckedArray] = useState<boolean[]>(
		new Array(filters.length).fill(false),
	);
	const handleChecked = (index: number) => {
		if (index === -1) {
			setCheckedArray(new Array(filters.length).fill(false));
			return;
		}
		const newCheckedArray = [...checkedArray];
		newCheckedArray[index] = !newCheckedArray[index];
		setCheckedArray(newCheckedArray);
	};

	useEffect(() => {
		dispatch(getAvailablePlansRequest());
		dispatch(getCurrentPlanRequest());
		dispatch(getRequestedPlanRequest());
	}, [dispatch]);

	useEffect(() => {
		if (currentPlan) {
			setCheckedArray([
				currentPlan.ioss_available,
				currentPlan.uk_available,
				currentPlan.oss_available,
			]);
		}
	}, [currentPlan]);

	if (window.location.origin.includes('2024.easproject.com')) {
		return (
			<Box
				sx={{
					margin: '10px 10px 50px 150px',
					maxWidth: '800px',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'start',
					justifyContent: 'start',
				}}
			>
				{/* <Typography sx={{ fontSize: '1rem', fontWeight: 400 }}>
					Please go to the Dashboard to access the Pricing Page
				</Typography> */}
				<Button
					sx={{
						marginTop: '10px',
					}}
					variant="contained"
					color="primary"
					component="a"
					href={SHOPIFY_PRICING_URL}
					target="_blank"
					rel="noopener noreferrer"
				>
					Go to the Pricing Page
				</Button>
			</Box>
		);
	}

	return (
		<Box>
			<Paper
				sx={{
					margin: '2px 10px 2px 10px',
					maxWidth: 'calc(100vw - 20px)',
					overflowX: 'auto',
					mb: 3,
				}}
			>
				<Box
					sx={{
						width: 'auto',
						p: '8px',
					}}
				>
					<BlockRequestBanner />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: 1,
						}}
					>
						<PlanFilter checkedArray={checkedArray} handleChecked={handleChecked} />
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'end',
								flexWrap: 'wrap',
								gap: 1,
							}}
						>
							{!requestedPlanLoader && requestedPlan && requestedPlanData && (
								<PricingCardSmall
									data={requestedPlanData}
									text={`Requested: ${DateTime.fromISO(requestedPlan?.requestedAt).toFormat(
										'yyyy-MM-dd',
									)}, `}
									text2={` Will apply: ${DateTime.fromISO(requestedPlan?.effectiveMonth).toFormat(
										'yyyy-MM-dd',
									)}`}
								/>
							)}

							{!currentPlanLoader && currentPlan && (
								<PricingCardSmall
									data={currentPlan}
									text="Current Plan"
									text2={currentPlan?.transaction_plan ? '(deprecated)' : ''}
								/>
							)}
							{currentPlanLoader && (
								<Skeleton
									variant="rectangular"
									height="100%"
									width={350}
									sx={{
										borderRadius: '12px',
										'& .MuiCardContent-root': { padding: '8px 16px 4px 16px!important' },
									}}
								/>
							)}
						</Box>
					</Box>
				</Box>
			</Paper>

			<Box
				sx={{
					margin: '10px 10px 30px 10px',
					width: 'auto',
				}}
			>
				<Box>
					<Box
						sx={{
							display: 'grid',
							justifyContent: 'center',
							gridTemplateColumns: {
								lg: 'repeat(3, minmax(360px, 440px))',
								md: 'repeat(2, minmax(360px, 440px))',
								xs: 'repeat(1,  minmax(360px, 440px))',
							},

							gap: 3,
						}}
					>
						{(!availablePlansLoader &&
							availablePlans
								?.filter((item) => {
									if (checkedArray.every((checked) => !checked)) return true;
									if (
										(checkedArray[0] &&
											!checkedArray[1] &&
											!checkedArray[2] &&
											item.pricing_group === 'IOSS or UK') ||
										(!checkedArray[0] &&
											checkedArray[1] &&
											!checkedArray[2] &&
											item.pricing_group === 'IOSS or UK') ||
										(!checkedArray[0] &&
											!checkedArray[1] &&
											checkedArray[2] &&
											item.pricing_group === 'OSS') ||
										(checkedArray[0] &&
											checkedArray[1] &&
											!checkedArray[2] &&
											item.pricing_group === 'IOSS + UK') ||
										(checkedArray[0] &&
											!checkedArray[1] &&
											checkedArray[2] &&
											item.pricing_group === 'IOSS or UK + OSS') ||
										(!checkedArray[0] &&
											checkedArray[1] &&
											checkedArray[2] &&
											item.pricing_group === 'IOSS or UK + OSS') ||
										(checkedArray[0] &&
											checkedArray[1] &&
											checkedArray[2] &&
											item.pricing_group === 'IOSS + UK + OSS')
									)
										return true;
									return false;
								})
								?.map((item) => <PricingCardBig key={item.id} data={item} />)) ?? (
							<Typography>No Available Plans</Typography>
						)}
						{availablePlansLoader &&
							[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4].map(() => (
								<Skeleton
									variant="rectangular"
									height={60}
									sx={{
										width: '100%',
										minHeight: 300,
										borderRadius: '12px',
										'& .MuiCardContent-root': { padding: '8px 16px 4px 16px!important' },
									}}
								/>
							))}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
